import produce from 'immer';
import _ from 'underscore';

import {
  isMultiImageNotePartGrid,
  takeContainedServerIdsFromClipboard,
} from 'notebook/notes/groups/note-part-grid/NotePartGridGroupConstants';
import {UnknownNoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {isNoteLinkedFile} from 'notebook/TypePredicates';
import {NotePart, NotePartWithItem} from 'notebook/util/NoteUtilConstants';

import {EditorMixinInterface} from '../Editor';

export default function convertNotePartGridForPaste(
  item: NotePartWithItem<UnknownNoteLinkedGroup>,
  component: Partial<EditorMixinInterface>
): void {
  // Server ids for contained files are smuggled through the clipboard by NoteLinkedGroupPlugin so a
  // cross-entry paste can resolve them (getIdFromCid only knows the destination entry's cidIdMap).
  // takeContainedServerIdsFromClipboard always strips the transient field (so it never reaches the
  // store or serialization); we only honor the map for multi-image grids, which are the only thing
  // NoteLinkedGroupPlugin stashes it for.
  const stashedContainedServerIds = takeContainedServerIdsFromClipboard(item);
  const containedServerIdsFromClipboard = isMultiImageNotePartGrid(item.item)
    ? stashedContainedServerIds
    : undefined;

  // Regenerate every contained note part's cid (and any contained NoteLinkedFile's cid) in a single
  // immer pass over the group, rather than a `produce` per contained part — `produce` isn't cheap,
  // and one draft of `item.item` covers the whole grid. Server ids resolved along the way are
  // batched into one registerObjects call below.
  const newCidToServerId: CidIdMap = {};
  item.item = produce(item.item, (draft) => {
    // containedNoteParts should always be present for a NOTE_PART_GRID, but guard against a malformed
    // group (e.g. a partial object from a bad clipboard payload) so the paste degrades to an empty
    // grid instead of throwing on `.filter`.
    draft.containedNoteParts = (draft.containedNoteParts ?? []).filter((np: NotePart) => np.item != null);
    for (const containedNotePart of draft.containedNoteParts) {
      containedNotePart.cid = _.uniqueId('note-part');

      // The type guard both gates on the contained item being a file and narrows it to
      // NoteLinkedFile for the `.cid` reads/writes below.
      const containedItem = containedNotePart.item;
      if (containedItem != null && isNoteLinkedFile(containedItem)) {
        const oldItemCid = containedItem.cid;
        // Same-entry paste resolves the server id via the shared cidIdMap; cross-entry paste falls
        // back to the clipboard-embedded map. Reusing the same server id (rather than cloning the
        // file server-side) matches the single-image paste contract in PreviewPluginFactory.
        const serverId =
          component.props!.getIdFromCid(oldItemCid) ?? containedServerIdsFromClipboard?.[oldItemCid];
        const newItemCid = _.uniqueId('note-linked-file-');
        containedItem.cid = newItemCid;
        if (serverId != null) {
          newCidToServerId[newItemCid] = serverId;
        }
      }
    }
  });

  if (!_.isEmpty(newCidToServerId)) {
    component.props!.registerObjects(newCidToServerId);
  }
}
